/* Desktop-only image fallback for submenu pages whose images live in sections. */
(() => {
  'use strict';
  if (window.hoqDesktopMenuImagesLoaded) return;
  window.hoqDesktopMenuImagesLoaded = true;

  const desktop = window.matchMedia('(min-width: 750px)');
  const menuSelector = '[data-header-desktop] .hoq-desktop-menu';
  const pageCache = new Map();
  const pending = [];
  let active = 0;

  function queue(task) {
    return new Promise((resolve) => {
      pending.push({ task, resolve });
      drain();
    });
  }

  function drain() {
    while (active < 2 && pending.length) {
      const job = pending.shift();
      active += 1;
      Promise.resolve().then(job.task).catch(() => []).then(job.resolve).finally(() => {
        active -= 1;
        drain();
      });
    }
  }

  function imageURL(value, base) {
    if (!value) return null;
    try {
      const url = new URL(value, base);
      if (!/^https?:$/.test(url.protocol)) return null;
      if (/\.(?:svg|gif)(?:$|\?)/i.test(url.pathname + url.search)) return null;
      if (/(?:logo|favicon|placeholder|transparent|payment[-_]|icon[-_]|swatch[-_])/i.test(url.pathname)) return null;
      return url.href;
    } catch (_) {
      return null;
    }
  }

  function sourceFor(img, base) {
    const width = parseInt(img.getAttribute('width'), 10);
    const height = parseInt(img.getAttribute('height'), 10);
    if ((width > 0 && width < 160) || (height > 0 && height < 120)) return null;
    const label = [img.getAttribute('alt'), img.className].join(' ');
    if (/\b(?:logo|icon|payment|badge|swatch)\b/i.test(label)) return null;
    const direct = img.getAttribute('data-src') || img.getAttribute('src');
    const srcset = img.getAttribute('data-srcset') || img.getAttribute('srcset') || '';
    const sources = srcset.split(',').map((part) => {
      const bits = part.trim().split(/\s+/);
      return { url: bits[0], width: parseInt(bits[1], 10) || 0 };
    }).filter((entry) => entry.width >= 360).sort((a, b) => a.width - b.width);
    for (const source of sources) {
      const url = imageURL(source.url, base);
      if (url) return url;
    }
    return imageURL(direct, base);
  }

  function candidatesFrom(html, base) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const main = doc.querySelector('main, [role="main"], #MainContent');
    const urls = [];
    const push = (value) => {
      if (value && !urls.includes(value)) urls.push(value);
    };
    if (main) {
      for (const img of main.querySelectorAll('img')) {
        if (img.closest('header, footer, nav, product-recommendations, [data-section-type*="recommend"], .popup, .modal')) continue;
        push(sourceFor(img, base));
        if (urls.length >= 3) break;
      }
      if (urls.length === 0) {
        for (const video of main.querySelectorAll('video[poster]')) {
          push(imageURL(video.getAttribute('poster'), base));
        }
      }
    }
    if (urls.length === 0) {
      const meta = doc.querySelector('meta[property="og:image"]');
      const candidate = imageURL(meta && meta.content, base);
      const current = document.querySelector('meta[property="og:image"]');
      // A repeated site-wide social image is not a submenu-specific image.
      if (!current || candidate !== imageURL(current.content, location.href)) push(candidate);
    }
    return urls.slice(0, 3);
  }

  function sourcesFor(rawURL) {
    let url;
    try { url = new URL(rawURL, location.href); } catch (_) { return Promise.resolve([]); }
    if (url.origin !== location.origin || !/\/(?:pages|collections|products|blogs)\//.test(url.pathname)) {
      return Promise.resolve([]);
    }
    // These are read-only page requests. Keep tracking strings out of requests/cache.
    url.hash = '';
    url.search = '';
    const key = url.href;
    if (!pageCache.has(key)) {
      pageCache.set(key, queue(async () => {
        if (!desktop.matches) return [];
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        try {
          const response = await fetch(key, {
            credentials: 'same-origin',
            headers: { Accept: 'text/html' },
            signal: controller.signal
          });
          if (!response.ok || !/text\/html/i.test(response.headers.get('content-type') || '')) return [];
          if (new URL(response.url || key).origin !== location.origin) return [];
          return candidatesFrom(await response.text(), response.url || key);
        } finally {
          clearTimeout(timeout);
        }
      }));
    }
    return pageCache.get(key);
  }

  function loadImage(src) {
    return new Promise((resolve) => {
      const img = new Image();
      const timer = setTimeout(() => finish(null), 8000);
      function finish(result) {
        clearTimeout(timer);
        img.onload = null;
        img.onerror = null;
        resolve(result);
      }
      img.alt = '';
      img.width = 500;
      img.height = 600;
      img.decoding = 'async';
      img.className = 'hoq-menu-card__image';
      img.onload = () => finish(img.naturalWidth >= 160 && img.naturalHeight >= 120 ? img : null);
      img.onerror = () => finish(null);
      img.src = src;
    });
  }

  async function hydrate(frame) {
    if (frame.dataset.hoqCardRequested) return;
    frame.dataset.hoqCardRequested = 'true';
    const sources = await sourcesFor(frame.dataset.hoqCardSource);
    for (const src of sources) {
      if (!frame.isConnected || !desktop.matches) {
        delete frame.dataset.hoqCardRequested;
        return;
      }
      const img = await loadImage(src);
      if (img && frame.isConnected) {
        frame.prepend(img);
        frame.closest('.hoq-menu-card').classList.add('hoq-menu-card--has-image');
        frame.removeAttribute('data-hoq-card-source');
        return;
      }
    }
    // A page without a usable image keeps a clean, clickable title card.
  }

  function activate(menu) {
    if (!desktop.matches || !menu) return;
    menu.querySelectorAll('[data-hoq-card-source]').forEach((frame) => {
      hydrate(frame).catch(() => {});
    });
  }

  function onInteraction(event) {
    if (!(event.target instanceof Element)) return;
    activate(event.target.closest(menuSelector));
  }

  document.addEventListener('pointerover', onInteraction, { passive: true });
  document.addEventListener('focusin', onInteraction);
  document.addEventListener('theme:disclosure:show', onInteraction, true);
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll(menuSelector + '.is-visible').forEach(activate);
  });
  document.querySelectorAll(menuSelector + '.is-visible').forEach(activate);
})();
